<template>
  <ModalComp
    :is-visible="isVisible"
    title="Confirmer la livraison gratuite"
    size="lg"
    :hide-footer="true"
    @update:is-visible="$emit('update:isVisible', $event)"
  >
    <template #body>
      <div class="space-y-5">
        <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <p class="font-semibold text-emerald-900">Livraison offerte par le vendeur</p>
          <p class="mt-1 text-sm text-emerald-800">
            Aucun identifiant de livraison eFashion n'est nécessaire. Ajoutez un numéro de suivi par colis : ils seront transmis au client.
          </p>
        </div>

        <div
          v-for="(parcel, index) in parcels"
          :key="parcel.key"
          class="space-y-3 rounded-lg border border-gray-200 p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <p class="text-sm font-semibold text-black">Colis {{ index + 1 }}</p>
            <button
              v-if="parcels.length > 1"
              type="button"
              class="text-sm text-red-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
              :disabled="loading"
              @click="removeParcel(index)"
            >
              Retirer
            </button>
          </div>

          <label class="block text-sm font-medium text-black">
            Numéro de suivi <span class="text-red-600">*</span>
            <input
              v-model="parcel.trackingNumber"
              type="text"
              autocomplete="off"
              class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 uppercase"
              placeholder="Ex. 1Z999AA10123456784"
              :disabled="loading"
              @input="onTrackingInput(parcel)"
            />
          </label>

          <label class="block text-sm font-medium text-black">
            Transporteur <span class="text-red-600">*</span>
            <select
              v-model.number="parcel.carrierId"
              class="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2"
              :disabled="loading"
            >
              <option :value="null" disabled>Sélectionner un transporteur</option>
              <option v-for="carrier in carriers" :key="carrier.id" :value="carrier.id">
                {{ carrier.label }}
              </option>
            </select>
            <p v-if="detectedCarrierLabel(parcel)" class="mt-1 text-xs text-blue-700">
              Transporteur détecté : {{ detectedCarrierLabel(parcel) }}. Vous pouvez le modifier si nécessaire.
            </p>
            <p v-else-if="parcel.trackingNumber" class="mt-1 text-xs text-amber-700">
              Détection incertaine : choisissez le transporteur correspondant.
            </p>
          </label>
        </div>

        <button
          type="button"
          class="text-sm font-medium text-blue-700 hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
          :disabled="loading || parcels.length >= maxParcels"
          @click="addParcel"
        >
          Ajouter un numéro de suivi
        </button>

        <div class="rounded-lg border border-gray-200 p-4">
          <p class="text-sm font-semibold text-black">Facture recommandée</p>
          <p class="mt-1 text-xs text-gray-600">
            Ajoutez-la si elle est disponible. Son absence n'empêche pas la confirmation.
          </p>
          <UploadZone
            :order-id="orderId"
            :order-group-id="orderGroupId"
            :order-date="orderDate"
            :invoice-id="invoiceId"
            @invoices-count-changed="invoiceCount = $event"
          />
          <p v-if="invoiceCount === 0" class="mt-2 text-xs text-amber-700">
            Aucune facture jointe : vous pouvez continuer et l'ajouter plus tard.
          </p>
        </div>

        <p v-if="validationError" class="text-sm font-medium text-red-600">
          {{ validationError }}
        </p>

        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            :disabled="loading"
            @click="$emit('update:isVisible', false)"
          >
            Annuler
          </button>
          <button
            type="button"
            class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            :disabled="loading"
            @click="confirm"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin mr-1"></i>
            {{ loading ? "Confirmation..." : "Confirmer la commande" }}
          </button>
        </div>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import ModalComp from "@/components/ui/ModalComp.vue";
import UploadZone from "@/components/order/UploadZone.vue";
import type { FreeShippingTrackingParcelInput } from "@/api/orders";
import {
  detectFreeShippingCarrier,
  FREE_SHIPPING_CARRIERS,
  normalizeTrackingNumber,
} from "@/utils/freeShippingTracking";

interface ParcelDraft {
  key: number;
  trackingNumber: string;
  carrierId: number | null;
  detectedCarrierId: number | null;
}

const props = defineProps<{
  isVisible: boolean;
  orderId: number;
  orderGroupId: number;
  orderDate: string;
  invoiceId?: string;
  loading?: boolean;
}>();

const emit = defineEmits<{
  "update:isVisible": [value: boolean];
  confirm: [
    payload: {
      trackingNumber: string;
      carrierId: number;
      parcels: FreeShippingTrackingParcelInput[];
    },
  ];
}>();

const carriers = FREE_SHIPPING_CARRIERS;
const maxParcels = 30;
let nextParcelKey = 1;

const createParcel = (): ParcelDraft => ({
  key: nextParcelKey++,
  trackingNumber: "",
  carrierId: null,
  detectedCarrierId: null,
});

const parcels = ref<ParcelDraft[]>([createParcel()]);
const invoiceCount = ref(0);
const validationError = ref("");

const detectedCarrierLabel = (parcel: ParcelDraft) => {
  if (!parcel.detectedCarrierId) return "";
  return (
    carriers.find((carrier) => carrier.id === parcel.detectedCarrierId)?.label ||
    ""
  );
};

const onTrackingInput = (parcel: ParcelDraft) => {
  validationError.value = "";
  const detected = detectFreeShippingCarrier(parcel.trackingNumber);
  if (
    parcel.carrierId == null ||
    parcel.carrierId === parcel.detectedCarrierId
  ) {
    parcel.carrierId = detected;
  }
  parcel.detectedCarrierId = detected;
};

const addParcel = () => {
  if (parcels.value.length >= maxParcels) return;
  parcels.value.push(createParcel());
};

const removeParcel = (index: number) => {
  if (parcels.value.length === 1) return;
  parcels.value.splice(index, 1);
};

watch(
  () => props.isVisible,
  (visible) => {
    validationError.value = "";
    if (!visible) return;
    nextParcelKey = 1;
    parcels.value = [createParcel()];
  },
);

const confirm = () => {
  const normalized: FreeShippingTrackingParcelInput[] = [];
  const seen = new Set<string>();

  for (const [index, parcel] of parcels.value.entries()) {
    let trackingNumber = normalizeTrackingNumber(parcel.trackingNumber);
    if (trackingNumber.length < 5) {
      validationError.value = `Renseignez un numéro de suivi valide pour le colis ${index + 1}.`;
      return;
    }
    if (!parcel.carrierId) {
      validationError.value = `Sélectionnez le transporteur du colis ${index + 1}.`;
      return;
    }
    if (parcel.carrierId === 10 && trackingNumber.startsWith("092")) {
      trackingNumber = trackingNumber.slice(3);
    }
    if (seen.has(trackingNumber)) {
      validationError.value = "Chaque colis doit avoir un numéro de suivi différent.";
      return;
    }
    seen.add(trackingNumber);
    normalized.push({
      trackingNumber,
      carrierId: parcel.carrierId,
    });
  }

  emit("confirm", {
    trackingNumber: normalized[0].trackingNumber,
    carrierId: normalized[0].carrierId,
    parcels: normalized,
  });
};
</script>
